import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BillingAuditAgreementLine,
  BillingAuditOverageCursor,
  BillingAuditTimeEntry,
  PaginatedResult,
  UpdateBillingAuditAgreementLineInput,
  UpdateBillingAuditOverageCursorInput,
  UpdateBillingAuditTimeEntryInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

const billingAuditKeys = {
  all: ['billing-audit'] as const,
  agreementLines: ['billing-audit', 'agreement-lines'] as const,
  overageCursors: ['billing-audit', 'overage-cursors'] as const,
  timeEntries: (page: number, pageSize: number, filters: TimeEntryFilters) =>
    ['billing-audit', 'time-entries', page, pageSize, filters] as const,
};

/** Server-side filters for the billable-time list. */
export interface TimeEntryFilters {
  workDateFrom?: string;
  workDateTo?: string;
  /** 'true' | 'false' | '' (any). */
  billable?: string;
  invoiced?: string;
  billedExternally?: string;
  writtenOff?: string;
  /** Case-insensitive contains matches. */
  member?: string;
  customer?: string;
  agreement?: string;
  project?: string;
  ticket?: string;
  cwId?: string;
}

export function useRefreshBillingAudit(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: billingAuditKeys.all }).then(() => undefined);
}

// --- Agreement lines -------------------------------------------------------

export function useBillingAuditAgreementLines(): UseQueryResult<BillingAuditAgreementLine[]> {
  return useQuery({
    queryKey: billingAuditKeys.agreementLines,
    queryFn: () => api.get<BillingAuditAgreementLine[]>('/billing-audit/agreement-lines'),
  });
}

export interface UpdateAgreementLineArgs {
  id: string;
  input: UpdateBillingAuditAgreementLineInput;
}

export function useUpdateBillingAuditAgreementLine(): UseMutationResult<
  BillingAuditAgreementLine,
  unknown,
  UpdateAgreementLineArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateAgreementLineArgs) =>
      api.patch<BillingAuditAgreementLine>(`/billing-audit/agreement-lines/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingAuditKeys.agreementLines });
    },
  });
}

// --- Overage cursors -------------------------------------------------------

export function useBillingAuditOverageCursors(): UseQueryResult<BillingAuditOverageCursor[]> {
  return useQuery({
    queryKey: billingAuditKeys.overageCursors,
    queryFn: () => api.get<BillingAuditOverageCursor[]>('/billing-audit/overage-cursors'),
  });
}

export interface UpdateOverageCursorArgs {
  agreementId: string;
  input: UpdateBillingAuditOverageCursorInput;
}

export function useUpdateBillingAuditOverageCursor(): UseMutationResult<
  BillingAuditOverageCursor,
  unknown,
  UpdateOverageCursorArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, input }: UpdateOverageCursorArgs) =>
      api.patch<BillingAuditOverageCursor>(
        `/billing-audit/overage-cursors/${agreementId}`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingAuditKeys.overageCursors });
    },
  });
}

// --- Time entries ----------------------------------------------------------

export function useBillingAuditTimeEntries(
  page: number,
  pageSize: number,
  filters: TimeEntryFilters = {},
): UseQueryResult<PaginatedResult<BillingAuditTimeEntry>> {
  return useQuery({
    queryKey: billingAuditKeys.timeEntries(page, pageSize, filters),
    queryFn: () =>
      api.get<PaginatedResult<BillingAuditTimeEntry>>(
        `/billing-audit/time-entries${buildQuery({
          page,
          pageSize,
          workDateFrom: filters.workDateFrom || undefined,
          workDateTo: filters.workDateTo || undefined,
          billable: filters.billable || undefined,
          invoiced: filters.invoiced || undefined,
          billedExternally: filters.billedExternally || undefined,
          writtenOff: filters.writtenOff || undefined,
          member: filters.member || undefined,
          customer: filters.customer || undefined,
          agreement: filters.agreement || undefined,
          project: filters.project || undefined,
          ticket: filters.ticket || undefined,
          cwId: filters.cwId || undefined,
        })}`,
      ),
  });
}

export interface UpdateTimeEntryBillingArgs {
  id: string;
  input: UpdateBillingAuditTimeEntryInput;
}

export function useUpdateBillingAuditTimeEntry(): UseMutationResult<
  BillingAuditTimeEntry,
  unknown,
  UpdateTimeEntryBillingArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTimeEntryBillingArgs) =>
      api.patch<BillingAuditTimeEntry>(`/billing-audit/time-entries/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingAuditKeys.all });
    },
  });
}
